import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  alpha,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const PICustomerSection = ({
  purchaseOrder,
  customers,
  onUpdate,
  companyId,
  canEdit = true
}) => {
  const theme = useTheme();
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  const handleSelectCustomer = (customer) => {
    onUpdate({
      customer_id: customer.id,
      customer_name: customer.name,
      customer_company: customer.company || '',
      billing_street: customer.street || '',
      billing_city: customer.city || '',
      billing_state: customer.state || '',
      billing_zip: customer.zip_code || '',
      billing_country: customer.country || ''
    });
    setCustomerDialogOpen(false);
  };

  const getFilteredCustomers = () => {
    if (!customerSearchTerm.trim()) return customers;
    const searchTerm = customerSearchTerm.toLowerCase();
    return customers.filter(customer => 
      customer.name?.toLowerCase().includes(searchTerm) ||
      customer.email?.toLowerCase().includes(searchTerm) ||
      customer.phone?.includes(searchTerm) ||
      customer.company?.toLowerCase().includes(searchTerm)
    );
  };

  return (
    <>
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6">
          <PersonIcon />
          Customer Information
        </SectionTitle>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => canEdit && setCustomerDialogOpen(true)}
              sx={{
                height: 56,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                justifyContent: 'flex-start',
                px: 3,
                py: 2.5,
                opacity: canEdit ? 1 : 0.3,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  {purchaseOrder.customer_id ? (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {purchaseOrder.customer_name || 'Unknown Customer'}
                      </Typography>
                      {purchaseOrder.customer_company && (
                        <Typography variant="caption" color="text.secondary">
                          {purchaseOrder.customer_company}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                      Select Customer
                    </Typography>
                  )}
                </Box>
              </Box>
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, fontSize: '1rem', color: 'primary.main' }}>
              Billing Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Street"
                  fullWidth
                  value={purchaseOrder.billing_street || ''}
                  onChange={(e) => canEdit && onUpdate({ billing_street: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  fullWidth
                  value={purchaseOrder.billing_city || ''}
                  onChange={(e) => canEdit && onUpdate({ billing_city: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="State"
                  fullWidth
                  value={purchaseOrder.billing_state || ''}
                  onChange={(e) => canEdit && onUpdate({ billing_state: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Zip"
                  fullWidth
                  value={purchaseOrder.billing_zip || ''}
                  onChange={(e) => canEdit && onUpdate({ billing_zip: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Country"
                  fullWidth
                  value={purchaseOrder.billing_country || ''}
                  onChange={(e) => canEdit && onUpdate({ billing_country: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, fontSize: '1rem', color: 'primary.main' }}>
              Shipping Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Street"
                  fullWidth
                  value={purchaseOrder.shipping_street || ''}
                  onChange={(e) => canEdit && onUpdate({ shipping_street: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  fullWidth
                  value={purchaseOrder.shipping_city || ''}
                  onChange={(e) => canEdit && onUpdate({ shipping_city: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="State"
                  fullWidth
                  value={purchaseOrder.shipping_state || ''}
                  onChange={(e) => canEdit && onUpdate({ shipping_state: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Zip"
                  fullWidth
                  value={purchaseOrder.shipping_zip || ''}
                  onChange={(e) => canEdit && onUpdate({ shipping_zip: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Country"
                  fullWidth
                  value={purchaseOrder.shipping_country || ''}
                  onChange={(e) => canEdit && onUpdate({ shipping_country: e.target.value })}
                  disabled={!canEdit}
                  InputProps={{
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  size="small"
                  onClick={() => {
                    if (!canEdit) return;
                    onUpdate({
                      shipping_street: purchaseOrder.billing_street,
                      shipping_city: purchaseOrder.billing_city,
                      shipping_state: purchaseOrder.billing_state,
                      shipping_zip: purchaseOrder.billing_zip,
                      shipping_country: purchaseOrder.billing_country,
                      same_as_billing: true
                    });
                  }}
                  sx={{ 
                    mt: 1,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    fontWeight: 500,
                    opacity: canEdit ? 1 : 0.3,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      transform: canEdit ? 'translateY(-1px)' : 'none',
                      boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}` : 'none'
                    }
                  }}
                >
                  Same as Billing
                </Button>
              </Grid>
            </Grid>
          </Grid>

        </Grid>
      </SectionPaper>

      {/* Customer Selection Dialog */}
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 2 }} />
            Select Customer
          </Box>
          <IconButton onClick={() => setCustomerDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search customers..."
              value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 500, width: 100 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredCustomers().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No customers found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredCustomers().map((customer) => (
                    <TableRow
                      key={customer.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{customer.name}</Typography>
                        {customer.company && (
                          <Typography variant="caption" color="text.secondary">{customer.company}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.email && <Typography variant="body2">{customer.email}</Typography>}
                        {customer.phone && <Typography variant="caption" color="text.secondary">{customer.phone}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {[customer.street, customer.city, customer.state].filter(Boolean).join(', ') || 'No address'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCustomer(customer);
                          }}
                          sx={{ borderRadius: 2 }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => window.open(`/company/${companyId}/customer-new`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
          >
            Add New Customer
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default PICustomerSection;